import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CurrencyPriceInput } from '@/components/form/currency-price-input'
import { SearchProductVariantInput } from '@/components/form/search-product-variant-input'
import { SearchProductListInput } from '@/components/form/search-product-list-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Rule } from './promotion-form'
import { useRowFieldErrors } from './use-row-field-errors'

const PAYMENT_METHOD_NAMES = ['paypal', 'card', 'klarna', 'zelle', 'crypto'] as const

type RuleType = 'ContainsProduct' | 'OrderAmountMinimum' | 'OrderMinQuantity' | 'OrderMaxQuantity' | 'MaxProductQuantity' | 'PaymentIntegrationSelected' | 'DoNotContainRecurringProduct' | 'MaxAmount' | 'ExcludeProduct'

interface RuleCardProps {
    rule: Rule
    index: number
    storeId: string
    onUpdate: (index: number, updates: Partial<Rule>) => void
    onRemove: (index: number) => void
}

export const RuleCard = ({ rule, index, storeId, onUpdate, onRemove }: RuleCardProps) => {
    const { t } = useTranslation(['stores', 'common'])
    const { hasFieldError, renderFieldError } = useRowFieldErrors('rules', index)

    return (
        <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-3">
                <h4 className="text-sm font-semibold">
                    {t(`promotion.rules.titles.${rule.type}`)}
                </h4>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(index)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>

            {rule.type === 'ContainsProduct' && (
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t('promotion.rules.product')}</Label>
                        <div className="mt-1">
                            <SearchProductVariantInput
                                storeId={storeId}
                                includeInactive
                                productId={rule.productId}
                                selectedVariantIds={rule.variantIds || []}
                                onChange={({ productId, variantIds }) =>
                                    onUpdate(index, { productId, variantIds })
                                }
                                onClear={() => onUpdate(index, { productId: undefined, variantIds: undefined })}
                            />
                        </div>
                        {renderFieldError('productId')}
                    </div>

                    <div>
                        <Label className="text-xs">{t('promotion.rules.minimumQuantity')}</Label>
                        <Input
                            type="number"
                            value={rule.minimumQuantity || 1}
                            onChange={(e) => onUpdate(index, { minimumQuantity: parseInt(e.target.value) || 1 })}
                            className="mt-1 h-8"
                            min={1}
                        />
                    </div>
                </div>
            )}

            {rule.type === 'OrderAmountMinimum' && (
                <div>
                    <Label className="text-xs">{t('promotion.rules.minimumAmount')}</Label>
                    <div className="mt-1">
                        <CurrencyPriceInput
                            storeId={storeId}
                            values={rule.minimumAmount || {}}
                            onChange={({ currencyOptions }) => onUpdate(index, { minimumAmount: currencyOptions })}
                        />
                    </div>
                    {renderFieldError('minimumAmount')}
                </div>
            )}

            {rule.type === 'OrderMinQuantity' && (
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t('promotion.rules.minQuantity')}</Label>
                        <Input
                            type="number"
                            value={rule.orderMinQuantity || 1}
                            onChange={(e) => onUpdate(index, { orderMinQuantity: parseInt(e.target.value) || 1 })}
                            className="mt-1 h-8"
                            min={1}
                            error={hasFieldError('orderMinQuantity')}
                        />
                        {renderFieldError('orderMinQuantity')}
                    </div>
                    <div>
                        <Label className="text-xs">{t('promotion.rules.excludedProducts')}</Label>
                        <div className="mt-1">
                            <SearchProductListInput
                                storeId={storeId}
                                includeInactive
                                selectedProductIds={rule.excludedProductIds || []}
                                onChange={(ids) => onUpdate(index, { excludedProductIds: ids })}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {t('promotion.rules.excludedProductsHelperText')}
                        </p>
                    </div>
                </div>
            )}

            {rule.type === 'OrderMaxQuantity' && (
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t('promotion.rules.maxQuantity')}</Label>
                        <Input
                            type="number"
                            value={rule.orderMaxQuantity || 1}
                            onChange={(e) => onUpdate(index, { orderMaxQuantity: parseInt(e.target.value) || 1 })}
                            className="mt-1 h-8"
                            min={1}
                            error={hasFieldError('orderMaxQuantity')}
                        />
                        {renderFieldError('orderMaxQuantity')}
                    </div>
                    <div>
                        <Label className="text-xs">{t('promotion.rules.excludedProducts')}</Label>
                        <div className="mt-1">
                            <SearchProductListInput
                                storeId={storeId}
                                includeInactive
                                selectedProductIds={rule.excludedProductIds || []}
                                onChange={(ids) => onUpdate(index, { excludedProductIds: ids })}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {t('promotion.rules.excludedProductsHelperText')}
                        </p>
                    </div>
                </div>
            )}

            {rule.type === 'PaymentIntegrationSelected' && (
                <div>
                    <Label className="text-xs">
                        {t('promotion.rules.paymentIntegration')}
                    </Label>
                    <div className="mt-1">
                        <Select
                            value={rule.paymentMethodName ?? ''}
                            onValueChange={(value) => onUpdate(index, { paymentMethodName: value as typeof PAYMENT_METHOD_NAMES[number] })}
                        >
                            <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder={t('common:formComponents.selectPaymentMethod')} />
                            </SelectTrigger>
                            <SelectContent>
                                {PAYMENT_METHOD_NAMES.map((name) => (
                                    <SelectItem key={name} value={name}>
                                        {name.charAt(0).toUpperCase() + name.slice(1)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {renderFieldError('paymentMethodName')}
                </div>
            )}

            {rule.type === 'MaxProductQuantity' && (
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t('promotion.rules.product')}</Label>
                        <div className="mt-1">
                            <SearchProductVariantInput
                                storeId={storeId}
                                includeInactive
                                productId={rule.productId}
                                selectedVariantIds={rule.variantIds || []}
                                onChange={({ productId, variantIds }) =>
                                    onUpdate(index, { productId, variantIds })
                                }
                                onClear={() => onUpdate(index, { productId: undefined, variantIds: undefined })}
                            />
                        </div>
                        {renderFieldError('productId')}
                    </div>

                    <div>
                        <Label className="text-xs">{t('promotion.rules.maximumQuantity')}</Label>
                        <Input
                            type="number"
                            value={rule.maximumQuantity || 1}
                            onChange={(e) => onUpdate(index, { maximumQuantity: parseInt(e.target.value) || 1 })}
                            className="mt-1 h-8"
                            min={1}
                            error={hasFieldError('maximumQuantity')}
                        />
                        {renderFieldError('maximumQuantity')}
                    </div>
                </div>
            )}

            {rule.type === 'MaxAmount' && (
                <div>
                    <Label className="text-xs">{t('promotion.rules.maximumAmount')}</Label>
                    <div className="mt-1">
                        <CurrencyPriceInput
                            storeId={storeId}
                            values={rule.maximumAmount || {}}
                            onChange={({ currencyOptions }) => onUpdate(index, { maximumAmount: currencyOptions })}
                        />
                    </div>
                    {renderFieldError('maximumAmount')}
                </div>
            )}

            {rule.type === 'DoNotContainRecurringProduct' && (
                <p className="text-muted-foreground text-xs">
                    {t('promotion.rules.doNotContainRecurringProductDescription')}
                </p>
            )}

            {rule.type === 'ExcludeProduct' && (
                <div>
                    <Label className="text-xs">{t('promotion.rules.excludeProduct.products')}</Label>
                    <div className="mt-1">
                        <SearchProductListInput
                            storeId={storeId}
                            includeInactive
                            selectedProductIds={rule.excludedProductIds || []}
                            onChange={(ids) => onUpdate(index, { excludedProductIds: ids })}
                        />
                    </div>
                    {renderFieldError('excludedProductIds')}
                    <p className="mt-1 text-xs text-gray-500">
                        {t('promotion.rules.excludeProduct.helperLine1')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        {t('promotion.rules.excludeProduct.helperLine2')}
                    </p>
                </div>
            )}
        </div>
    )
}

